<template>
  <!-- One order on phones and PCs (SPEC §6.1): board, chart, icons and position, then the button
       rows; each control keeps its place when the mode changes (plan 2026-10-28-replay-layout.md).
       On a wide screen all but the board sit in one column beside it, in the same order. -->
  <div class="replay" :class="{ side, desktop: !$store.state.isMobile, practice: Boolean(practice) }">
    <GameBoard ref="gameBoard"
               class="board"
               :state="state"
               :size="boardSize"
               :playMode="playMode"
               @action="onAction"
               />

    <div class="panel">

    <!-- The chart's place keeps its height, a note where there is no analysis; practice and 试下
         have no chart (their own layout). -->
    <div v-if="!practice && !study" class="chart-slot" :class="{ empty: !showWinrates }">
      <WinrateChart v-if="showWinrates" class="winrate-chart" :game="game" :cursor="compare ? compare.c.p : line ? line.from : cursor" @update:cursor="chartCursor"/>
      <!-- A finished game without analysis can be sent to the analysis service (SPEC §6.6). -->
      <div v-else class="analysis-none">
        <span v-if="game.analysisRequest">{{ t('analysis-requested') }}</span>
        <template v-else>
          <span>{{ t('analysis-none') }}</span>
          <button type="button" class="analysis-request btn-secondary" :disabled="requesting" @click="requestAnalysis">{{ t('analysis-request') }}</button>
        </template>
      </div>
    </div>

    <div class="nav-row">
      <!-- In practice the icons only step through 看答案's line. -->
      <div v-if="!practice || compare || side" class="nav-col">
        <!-- The six icons are always there, greyed where they cannot move (SPEC §6.1); each is
             named by its text. -->
        <div class="replay-bar">
          <button type="button" :aria-label="t('replay-reset')" :title="t('replay-reset')" :disabled="navOff || lineAtStart" @click="reset"><svg viewBox="0 0 20 20"><rect x="4" y="4" width="2.5" height="12"/><path d="M16 4 L8 10 L16 16 Z"/></svg></button>
          <button type="button" :aria-label="t('replay-back-5')" :title="t('replay-back-5')" :disabled="navOff || Boolean(line)" @click="moveCursor(-5)"><svg viewBox="0 0 20 20"><path d="M14 4 L6 10 L14 16 Z"/></svg><span>5</span></button>
          <button type="button" :aria-label="t('replay-back-1')" :title="t('replay-back-1')" :disabled="navOff || lineAtStart" @click="moveCursor(-1)"><svg viewBox="0 0 20 20"><path d="M14 4 L6 10 L14 16 Z"/></svg></button>
          <button type="button" :aria-label="t('replay-forward-1')" :title="t('replay-forward-1')" :disabled="navOff || Boolean(study) || lineAtEnd" @click="moveCursor(1)"><svg viewBox="0 0 20 20"><path d="M6 4 L14 10 L6 16 Z"/></svg></button>
          <button type="button" :aria-label="t('replay-forward-5')" :title="t('replay-forward-5')" :disabled="navOff || Boolean(study) || Boolean(line)" @click="moveCursor(5)"><span>5</span><svg viewBox="0 0 20 20"><path d="M6 4 L14 10 L6 16 Z"/></svg></button>
          <button type="button" :aria-label="t('replay-last')" :title="t('replay-last')" :disabled="navOff || Boolean(study) || lineAtEnd" @click="setCursor(-1)"><svg viewBox="0 0 20 20"><path d="M4 4 L12 10 L4 16 Z"/><rect x="13.5" y="4" width="2.5" height="12"/></svg></button>
        </div>
        <!-- The position, and the chart's switches while the chart is shown. -->
        <div class="position-row">
          <span class="position">{{ positionText }}</span>
          <span v-if="!practice && !study && showWinrates" class="chart-switches">
            <button type="button" class="suggestions-switch chart-line-switch" @click="switchChartLine">{{ t(chartLine === 'lead' ? 'chart-switch-to-winrate' : 'chart-switch-to-lead') }}</button><span class="separator">·</span><button type="button" class="suggestions-switch" @click="switchSuggestions">{{ t(suggestionsOn ? 'suggestions-hide' : 'suggestions-show') }}</button>
          </span>
        </div>
      </div>

      <!-- The mode row: four cells of one size, under the position on a phone, beside the icons on
           a PC. In 试下 with the AI it holds the AI control. -->
      <div v-if="aiControl" class="ai-control">
        <div class="ai-box" :class="'turn-' + (aiControl.halves[0].turn ? 'left' : 'right')">
          <div v-for="(h, k) in aiControl.halves" :key="h.key" role="group" :aria-label="t('study-ai-group', { color: h.name })"
               class="ai-half" :class="[h.key, { turn: h.turn, right: k === 1 }]">
            <button type="button" role="switch" :aria-checked="String(h.auto)" class="ai-switch" :class="{ on: h.auto }" @click="toggleAuto(h.stone)">
              <span class="knob"></span><span class="label">{{ t(h.auto ? 'study-ai-auto' : 'study-ai-manual') }}</span>
            </button>
          </div>
          <span v-if="aiControl.middle === 'thinking'" class="ai-middle ai-thinking" :class="aiControl.turnKey" role="status">{{ t('study-ai-thinking', { color: aiControl.toMove }) }}<span class="spinner"></span></span>
          <button v-else-if="aiControl.middle === 'continue'" type="button" class="ai-middle ai-continue" :class="aiControl.turnKey" @click="aiPlay">{{ t('study-ai-continue') }}</button>
          <button v-else type="button" class="ai-middle ai-move" :class="aiControl.turnKey" @click="aiPlay">{{ t('study-ai-play', { color: aiControl.toMove }) }}</button>
        </div>
      </div>
      <div v-else-if="practice" class="mode-row">
        <template v-if="!practice.done">
          <button type="button" class="btn-secondary practice-prev" :disabled="practice.i === 0" @click="practisePrev">{{ t('practise-prev') }}</button>
          <button v-if="practice.guess && !practiceGood && !compare" type="button" class="btn-main practice-again" @click="practiseAgain">{{ t('practise-again') }}</button>
          <button v-else-if="compare || practice.guess || practice.results[practice.i]" type="button" class="btn-main practice-next" @click="practiseNext">{{ t('practise-next') }}</button>
          <button v-else type="button" class="btn-secondary practice-skip" @click="practiseNext">{{ t('practise-skip') }}</button>
          <button v-if="compare" type="button" class="btn-secondary mode-switch line-back" @click="closeCompare">{{ t('practise-back-question') }}</button>
          <button v-else type="button" class="btn-secondary mode-switch" :class="practice.guess && practiceGood ? 'practice-compare' : 'practice-answer'" :disabled="!practiceComparable" @click="practiseAnswer">{{ t(practice.guess && practiceGood ? 'analysis-compare' : 'practise-answer') }}</button>
          <button type="button" class="btn-secondary mode-switch practice-end" @click="endPractice">{{ t('practise-end') }}</button>
        </template>
        <!-- The end: on a PC the four stay, greyed, and 再练一遍 / 返回对局 go to the action row. -->
        <template v-else-if="side">
          <button type="button" class="btn-secondary" disabled>{{ t('practise-prev') }}</button>
          <button type="button" class="btn-secondary" disabled>{{ t('practise-next') }}</button>
          <button type="button" class="btn-secondary mode-switch" disabled>{{ t('practise-answer') }}</button>
          <button type="button" class="btn-secondary mode-switch" disabled>{{ t('practise-end') }}</button>
        </template>
        <template v-else>
          <span></span>
          <button type="button" class="btn-secondary practice-restart" @click="startPractice">{{ t('practise-restart') }}</button>
          <span></span>
          <button type="button" class="btn-main practice-back" @click="endPractice">{{ t('practise-back') }}</button>
        </template>
      </div>
      <!-- The previous / next mistake, the mistake's views and 练习 (SPEC §6.6, §6.7). -->
      <div v-else-if="showWinrates" class="mode-row">
        <button type="button" class="btn-secondary mistake-prev" :disabled="!mistakeNav || mistakeNav.prev === null" @click="jumpMistake(-1)">{{ t('mistake-prev') }}</button>
        <button type="button" class="btn-secondary mistake-next" :disabled="!mistakeNav || mistakeNav.next === null" @click="jumpMistake(1)">{{ t('mistake-next') }}</button>
        <button v-if="line || compare" type="button" class="btn-main line-back" @click="compare ? closeCompare() : closeLine()">{{ t('analysis-line-back') }}</button>
        <button v-else type="button" class="btn-secondary mode-switch analysis-compare" :disabled="!comparable" @click="openCompare">{{ t('analysis-compare') }}</button>
        <button type="button" class="btn-secondary mode-switch practise" :disabled="!canPractise" @click="startPractice">{{ t('practise') }}</button>
      </div>
    </div>
    <!-- Practice's texts (SPEC §6.7): on a phone under its buttons; on a PC in the chart's place,
         cut to its height with … (a tap shows all of it over the buttons, a tap elsewhere closes). -->
    <div v-if="practice" ref="practiceInfo" class="practice-info" :class="{ open: practiceOpen }">
      <div ref="practiceBox" class="practice-box">
      <!-- 看答案: the mistake's views (SPEC §6.6); on a phone 试下 beside the tabs. -->
      <CompareInfo v-if="compare" :compare="compare" :heading="compareHeading" :what="compareWhat" :labels="compareLabels"
                   :maps="compareMaps" :squares="Boolean(state.squares)" :step="line ? line.n : 0" @tab="showCompareTab">
        <button v-if="!side" type="button" class="btn-secondary line-study tabs-study" :disabled="!line" @click="startStudy">{{ t('line-study') }}</button>
      </CompareInfo>
      <!-- Practice: find a better move at the position before each mistake (SPEC §6.7). -->
      <div v-if="practising" class="practice">
        <div class="move-hint practice-head">{{ practiceHead }}</div>
        <template v-if="!practice.done">
          <div class="move-line practice-question">{{ practiceQuestion.before }}<span class="mistake">{{ practiceQuestion.points }}</span>{{ practiceQuestion.after }}</div>
          <div v-if="!practice.guess" class="move-hint">{{ t('practise-ask') }}</div>
          <div v-else class="practice-verdict" :class="{ good: practiceGood }">{{ practiceVerdictText }}<button v-if="!side && aiOffered" type="button" class="btn-secondary practise-study verdict-study" @click.stop="startStudy">{{ t('practise-study') }}</button></div>
          <div v-if="practiceHint" class="move-hint">{{ practiceHint }}</div>
        </template>
        <template v-else>
          <div class="practice-verdict">{{ practiceTally }}</div>
          <div v-for="(r, i) in practiceRows" :key="i" class="move-hint practice-row">{{ r }}</div>
        </template>
      </div>
      </div>
      <button v-if="side && practiceCut && !practiceOpen" type="button" class="practice-more" :aria-label="t('practise-more')" @click.stop="practiceOpen = true">…</button>
    </div>

    <div v-if="(study || practising) && selected && playMode==='button'" class="placement">
      <div tabIndex="0" class="put btn-main" @click="buttonPut">{{ t('button-place-stone') }}</div>
    </div>

    <!-- What the board shows: the move's result, the view, the question or the AI's answers. -->
    <div class="info" :class="{ reserve: !practice }">
    <!-- How the shown move fared (SPEC §6.6). -->
    <div v-if="moveLine" class="move-line">{{ moveLine.before }}<span v-if="moveLine.points !== null" :class="{ mistake: moveLine.mistake }">{{ moveLine.points }}</span>{{ moveLine.after }}</div>
    <div v-if="lineHintShown" class="move-hint">{{ t('analysis-line-hint') }}</div>
    <!-- The 实战 / 推荐 / 对比 views of a mistake (SPEC §6.6), outside practice. -->
    <CompareInfo v-if="compare && !practice" :compare="compare" :heading="compareHeading" :what="compareWhat" :labels="compareLabels"
                 :maps="compareMaps" :squares="Boolean(state.squares)" :step="line ? line.n : 0" @tab="showCompareTab"/>
    <!-- A suggestion's line (SPEC §6.6), and the previous / next suggestion's line. -->
    <template v-if="line && !compare">
      <div class="move-line line-heading">{{ lineHeading }}</div>
      <div class="line-switch">
        <button type="button" class="btn-secondary" :aria-label="t('analysis-line-prev')" :title="t('analysis-line-prev')" :disabled="!neighbourLine(-1)" @click="switchLine(-1)"><svg viewBox="0 0 20 20"><path d="M4 14 L10 6 L16 14 Z"/></svg></button>
        <span class="line-rank">{{ t('analysis-line-switch', { rank: line.rank, shown: line.shown.length }) }}</span>
        <button type="button" class="btn-secondary" :aria-label="t('analysis-line-next')" :title="t('analysis-line-next')" :disabled="!neighbourLine(1)" @click="switchLine(1)"><svg viewBox="0 0 20 20"><path d="M4 6 L10 14 L16 6 Z"/></svg></button>
      </div>
      <div v-if="line.total < SHORT_LINE" class="move-hint">{{ t('analysis-line-short') }}</div>
    </template>

    <!-- 试下 from a line or a view, and the AI's answers in 试下 (SPEC §6.4). -->
    <div v-if="study && (study.from || study.ai)" class="study-ai">
      <div v-if="study.from" class="study-from">{{ study.from }}</div>
      <template v-if="study.ai">
        <div v-if="study.ai.error" class="study-ai-line study-ai-error">
          {{ t(study.ai.error.tag, { reason: study.ai.error.reason }) }}
          <button v-if="study.ai.error.retry" type="button" class="btn-secondary ai-retry" @click="aiPlay">{{ t('ai-retry') }}</button>
        </div>
        <div v-if="study.ai.error && study.ai.error.hint" class="move-hint">{{ t(study.ai.error.hint) }}</div>
        <div v-if="aiAsk" class="study-ai-line study-ai-ask">{{ aiAsk }}</div>
        <div v-if="study.ai.judged" class="study-ai-line study-ai-judged" :class="{ good: study.ai.judged.kind !== 'lost' }">
          <template v-if="study.ai.judged.kind === 'lost'">{{ study.ai.judged.before }}<span :class="{ mistake: study.ai.judged.mistake }">{{ study.ai.judged.points }}</span>{{ study.ai.judged.after }}</template>
          <template v-else>{{ study.ai.judged.text }}</template>
        </div>
        <div v-if="study.ai.reply" class="study-ai-line study-ai-reply">{{ study.ai.reply }}</div>
        <div v-for="l in study.ai.labels" :key="l" class="compare-label">{{ l }}</div>
      </template>
    </div>
    <!-- KataGo's estimate (SPEC §6.3), or waiting for the site's. -->
    <template v-if="katagoEstimate !== null">
      <div class="estimate-katago">{{ t('estimate-katago', { lead: leadText(katagoEstimate) }) }}</div>
      <div class="move-hint">{{ t('estimate-katago-open') }}</div>
    </template>
    <div v-else-if="estimateWaiting" class="move-hint">{{ t('estimate-waiting') }}</div>
    <div v-if="estimateText" class="estimate-count">{{ estimateText }}</div>
    </div>

    <!-- The action row: 形势判断, 试下 / 结束试下, 保存变化, 聊天; each always in its cell. -->
    <div v-if="!practice || side" class="action-row">
      <button type="button" class="btn-secondary" :disabled="studyThinking || Boolean(practice) || Boolean(compare)" @click="estimate">{{ t('button-estimate') }}</button>
      <button v-if="study" type="button" class="btn-secondary" @click="toggleStudy">{{ t('replay-end-study') }}</button>
      <button v-else-if="practice && !compare" type="button" class="btn-secondary practise-study" :disabled="!aiOffered || !practice.guess || practice.done" @click="startStudy">{{ t('practise-study') }}</button>
      <button v-else-if="line" type="button" class="btn-secondary line-study" @click="startStudy">{{ t('line-study') }}</button>
      <button v-else type="button" class="btn-secondary" :disabled="showingVariationFrom !== null || Boolean(compare)" @click="toggleStudy">{{ t('replay-study') }}</button>
      <!-- Practice (on a PC) has no 聊天; at its end 再练一遍 and 返回对局 take the last two cells. -->
      <template v-if="practice && practice.done">
        <button type="button" class="btn-secondary mode-switch practice-restart" @click="startPractice">{{ t('practise-restart') }}</button>
        <button type="button" class="btn-main practice-back" @click="endPractice">{{ t('practise-back') }}</button>
      </template>
      <template v-else>
        <button v-if="study" type="button" class="btn-secondary" :disabled="studyThinking" @click="saveVariation">{{ t('replay-save-variation') }}</button>
        <span v-else></span>
        <ChatButton v-if="chat && !practice" class="chat-btn" :pressed="chat.shown" @click="$emit('chat-toggle')"/>
      </template>
    </div>
    <PlayModeToggle/>
    <div v-if="savingVariation">
      备注:
      <textarea v-model="varDesc" rows=4 cols=40 /><br/>
      <button type="button" class="btn-main" :disabled="postingVariation" @click="postVariation">{{ t('button-save') }}</button>
      <button type="button" class="btn-secondary" :disabled="postingVariation" @click="cancelSaveVariation">{{ t('dialog-cancel') }}</button>
    </div>

    <div v-if="showingVariationFrom !== null">
      <span style="white-space: pre;">{{ varDesc }}</span>
    </div>
    <!-- The chat panel on a desktop, under the replay's buttons (SPEC §5.6). -->
    <slot name="chat"></slot>
    </div>
  </div>
</template>

<script>
import Game from '@/game/Game';
import { Stone } from '@/game/Board';
import GameBoard, { State, InfluenceChain, InputValidator, Action } from './GameBoard';
import WinrateChart from './WinrateChart';
import PlayModeToggle from './PlayModeToggle';
import ChatButton from './ChatButton';
import CompareInfo from './CompareInfo';
import Session from '@/session/Session';
import { SimpleStoneChain } from './GameBoard/StoneChain';
import { SimpleMarkChain } from './GameBoard/MarkChain';
import { deepCopy, desktopBoardSize } from '@/utils';
import api, { RestError } from '@/api/api';
import { t, errorText } from '@/texts';
import * as analysis from '@/game/analysis';

// While an analysis is awaited, the game is read again this often (ms).
const POLL_MS = 20000;
// It stops after this long without new analyses (the analysis service may be down); a reload starts again.
const POLL_GIVE_UP_MS = 10 * 60 * 1000;

class ReplayStoneChain extends SimpleStoneChain {
  constructor(moves, initCursor) {
    super(moves);
    this.cursor = initCursor;
  }

  *iterate() {
    yield* this.iterateUntil(this.cursor);
  }
}

class VariationSelectorInputValidator extends InputValidator {
  constructor(game, nextMove=null, variations=null) {
    super();
    this.game = game;
    this.nextMove = nextMove;
    this.variations = variations;
  }

  get inputType() {
    if(!this.variations) return null;
    return this.variations[0].moves[0].stone;
  }

  validate(point) {
    const m = this.game.encode(point.x, point.y);
    if(m === this.nextMove.move)
      return true;

    if(this.variations) {
      for(const v of this.variations) {
        if(m === v.moves[0].move)
          return true;
      }
    }

    return false;
  }
}

// Practice's input: one stone at the question's position, until the guess is judged.
class PracticeInput extends InputValidator {
  constructor(practice) {
    super();
    this.practice = practice;
  }

  get inputType() {
    return this.practice.guess ? null : this.practice.game.nextPlayer;
  }

  validate(point) {
    return !this.practice.guess && this.practice.game.canPlay(point);
  }
}

// A study (试下, SPEC §6.4): stones placed on a copy of the position shown. It starts after `start`
// records of `base` (the game, or a line's game): the main line's first `mainStart` records, then
// the line's moves, if any. With `ai` (accounts the admin let use the AI) the AI can play either
// colour (plan 2026-10-27-katago-replies.md).
class Study {
  constructor(game, start, opts = {}) {
    this.game = game;
    this.start = start;
    this.mainStart = opts.mainStart === undefined ? start : opts.mainStart;
    // The head #study-from-line when it starts in a line or a view, else null.
    this.from = opts.from || null;
    // What 结束试下 shows again (see GameReplay.startStudy), or null: the main line.
    this.back = opts.back || null;
    this.expGame = (opts.base || game).clone();
    this.expGame.resetTo(start);
    this.ai = opts.ai || null;

    this.stoneChain = new class extends SimpleStoneChain {
      constructor(study) {
        super(study.expGame.moves);
        this.study = study;
      }

      *iterate() {
        for(const [i, m] of super.iterate()) {
          const s = deepCopy(m);
          s.stone = m.stone;

          if(m.index < this.study.start)
            s.index = 0;
          else
            s.index -= (this.study.start - 1);

          yield [i, s];
        }
      }
    }(this);

    this.input = new class extends InputValidator {
      constructor(study) {
        super();
        this.study = study;
      }

      // No stone while the AI thinks.
      get inputType() {
        return this.study.thinking ? null : this.study.expGame.nextPlayer;
      }

      validate(point) {
        return !this.study.thinking && this.study.expGame.canPlay(point);
      }
    }(this);
  }

  get thinking() {
    return Boolean(this.ai && this.ai.thinking);
  }

  // The study's own stones (not the line's before them).
  get stones() {
    return this.expGame.moves.length - this.start;
  }

  // The number a study stone shows (from 1).
  number(m) {
    return m.index - this.start + 1;
  }

  put(point, by) {
    if(!this.expGame.play(point)) return false;
    if(by) this.expGame.lastMove.by = by;
    return true;
  }

  // The moves after the main line, for the site: an estimate's or a search's variationMoves.
  variationMoves() {
    return this.expGame.moves.slice(this.mainStart).map(m => {
      const v = Session.convertMove(m);
      if(v.move === '.pass') v.move = 'pass';
      if(m.by) v.by = m.by;
      return v;
    });
  }

  getVariation(name, description) {
    if(this.expGame.moves.length === this.start)
      return null;

    return {
      name,
      description,
      moves: this.expGame.moves.slice(this.mainStart).map(m => {
        const v = Session.convertMove(m, 1 - this.mainStart);
        if(m.by) v.by = m.by;
        return v;
      })
    };
  }
}

// A suggestion's line (SPEC §6.6): the main line up to the shown position (the first `from`
// records), then the moves KataGo expects, numbered from 1 whatever the move-number setting.
class LineView {
  constructor(game, from, suggestion, shown) {
    this.from = from;
    this.suggestion = suggestion;
    this.rank = suggestion.rank;
    this.shown = shown;
    const g = game.clone();
    g.resetTo(from);
    for(const m of suggestion.line) {
      if(!g.play(game.decode(m))) break;
    }
    this.total = g.moves.length - from;
    // The main line to `from`, then the line: where a study from the shown step starts.
    this.lineGame = g;

    this.stoneChain = new class extends ReplayStoneChain {
      *iterate() {
        for(const [i, m] of super.iterate()) {
          const s = deepCopy(m);
          s.stone = m.stone;
          s.index = m.index < from ? 0 : m.index - from + 1;
          yield [i, s];
        }
      }
    }(g.moves, from + 1);
    this.stoneChain.numbered = true;
  }

  // The line's move shown, from 1.
  get n() {
    return this.stoneChain.cursor - this.from;
  }
}

// Lines shorter than this say that KataGo searched no further (#analysis-line-short).
const SHORT_LINE = 5;

export default {
  name: 'GameReplay',

  components: {
    GameBoard,
    WinrateChart,
    PlayModeToggle,
    ChatButton,
    CompareInfo,
  },

  props: {
    game: { type: Game, required: true },
    // The viewer's colour in this game (Stone), or null: which mistakes 练习 asks about.
    player: { default: null },
    // The game's chat (chat.js), for the 聊天 button.
    chat: { type: Object, default: null },
  },

  watch: {
    shownSuggestions() {
      this.applySuggestions();
    },

    'game.analysisRequest'() {
      this.pollAnalysis();
    },

    game() {
      this.pollAnalysis();
      if(this.study && this.study.back)
        this.stopStudy();
      if(this.practice)
        this.endPractice();
      if(this.compare)
        this.closeCompare();
      else if(this.line)
        this.closeLine();
      else if(this.study)
        this.stopStudy();
      else if(this.showingVariationFrom !== null)
        this.stopShowingVariation();
      else {
        this.state.stoneChain = new ReplayStoneChain(this.game.moves, this.cursor);
        this.moveCursor(0);
      }
    },
  },

  data() {
    return {
      study: null,
      selected: null,
      // The site's estimate asked for and not yet answered (estimatePosition()), or null.
      estimating: null,
      state: new State(this.game.grid, new ReplayStoneChain(this.game.moves, this.game.moves.length), new SimpleMarkChain(this.game), null, new VariationSelectorInputValidator(this.game)),
      showingVariationFrom: null,
      // The suggestion's line shown (a LineView), or null; in the 实战 / 推荐 views, their line.
      line: null,
      // The 实战 / 推荐 / 对比 views of a mistake: { c: analysis.comparison, tab, board }, or null.
      compare: null,
      // Practice (SPEC §6.7): { list, i, results, tries, guess, answered, done, game, from }, or null.
      practice: null,
      SHORT_LINE,

      savingVariation: false,
      postingVariation: false,
      varDesc: null,
      requesting: false,
      // Practice's texts on a PC: whether they are cut to the chart's height, and shown in full.
      practiceCut: false,
      practiceOpen: false,
    }
  },

  mounted() {
    this.applySuggestions();
    this.pollAnalysis();
    this.closePractice = e => {
      if(this.practiceOpen && this.$refs.practiceInfo && !this.$refs.practiceInfo.contains(e.target)) this.practiceOpen = false;
    };
    document.addEventListener('click', this.closePractice, true);
  },

  updated() {
    this.measurePractice();
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closePractice, true);
    clearTimeout(this.pollTimer);
    this.pollTimer = null;
    this.destroyed = true;
  },

  computed: {
    // On a desktop the board fits the window's height, below the nav bar and the game line (SPEC §7.1).
    boardSize() {
      if (this.$store.state.isMobile) return 0;
      // Beside it the column (at least 380 px, a 28 px gap and the margins) also fits the width.
      return this.side ? Math.min(desktopBoardSize(120), window.innerWidth - 440) : desktopBoardSize(330);
    },
    // A window wide enough for the controls beside the board.
    side() {
      return !this.$store.state.isMobile && window.innerWidth >= 1000;
    },

    playMode() {
      return (this.$store.state.ui && this.$store.state.ui.playMode) || 'button';
    },

    cursor() {
      return this.state.stoneChain.cursor;
    },

    // The AI in 试下, for the accounts the admin turned it on for (SPEC §6.4).
    aiOffered() {
      const u = this.$store.state.user;
      return Boolean(u && u.aiStudy);
    },

    aiAutoMoves() {
      return (this.$store.state.site && this.$store.state.site.aiAutoMoves) || 10;
    },

    // The AI control of 试下: my colour's half first; the half of the colour to move is wider and
    // holds the middle item (AI 下, the thinking status, or 继续).
    aiControl() {
      const ai = this.study && this.study.ai;
      if(!ai) return null;
      const toMove = this.study.expGame.nextPlayer;
      const half = stone => {
        const key = this.colorKey(stone);
        return { stone, key, name: this.colorName(stone), auto: ai.auto[key], turn: stone === toMove };
      };
      const other = ai.mine === Stone.BLACK ? Stone.WHITE : Stone.BLACK;
      const middle = ai.thinking ? 'thinking' : ai.paused ? 'continue' : 'play';
      return { halves: [half(ai.mine), half(other)], middle, toMove: this.colorName(toMove), turnKey: this.colorKey(toMove) };
    },

    studyThinking() {
      return Boolean(this.study && this.study.thinking);
    },

    // The six icons are greyed while the AI thinks, at a practice question and in 对比 (no line).
    chartLine() {
      return (this.$store.state.ui && this.$store.state.ui.chartLine) === 'lead' ? 'lead' : 'winrate';
    },

    navOff() {
      return this.studyThinking || (Boolean(this.practice) && !this.compare) || (Boolean(this.compare) && !this.line);
    },

    // The line under the icons: the main-line position, the step of a line or view, the study's
    // stones, or the position a practice question shows.
    positionText() {
      const total = this.game.moves.length - 1;
      if(this.line) return t('analysis-line-step', { n: this.line.n, total: this.line.total });
      if(this.study) return t('study-position', { n: this.study.start - 1, k: this.study.stones });
      if(this.practice && !this.practice.done && !this.compare) return t('replay-position', { n: this.practiceItem.index - 1, total });
      // A move sealed at a break says so (plan 2026-10-29-sealed-moves.md).
      const m = this.game.moves[this.cursor - 1];
      const sealed = m && m.sealedAt ? ` · ${t('replay-sealed')}` : '';
      return t('replay-position', { n: this.cursor - 1, total }) + sealed;
    },

    // ⑨ before the study's first stone, while the colour to move is by hand.
    aiAsk() {
      const st = this.study;
      if(!st || !st.ai || st.stones > 0 || st.thinking || st.ai.error) return null;
      const color = this.colorName(st.expGame.nextPlayer);
      return st.ai.auto[this.colorKey(st.expGame.nextPlayer)] ? null : t('study-ai-ask', { color });
    },

    // Where a saved variation or a suggestion's line leaves the main line (records before it), or null.
    varFrom() {
      if(this.line) return this.line.from;
      return this.showingVariationFrom;
    },

    estimateText() {
      if(this.katagoEstimate !== null) return '';
      if(this.state.infChain && this.state.infChain.index === (this.study ? this.study.start : this.cursor))
        return `黑:${this.state.infChain.black}, 白:${this.state.infChain.white}`;
      else
        return "";
    },

    // The chart shows when any position has KataGo's analysis (SPEC §9).
    showWinrates() {
      return this.game && this.game.moves && analysis.any(this.game);
    },

    // The main-line position shown, for the analysis (null in a study or a variation): the cursor's
    // record, or the last stone's after a resignation.
    shownPosition() {
      if(this.study || this.showingVariationFrom !== null || this.line || this.compare || this.practice) return null;
      return Math.max(0, Math.min(this.cursor - 1, analysis.lastPosition(this.game)));
    },

    suggestionsOn() {
      return !(this.$store.state.ui && this.$store.state.ui.suggestions === 'hide');
    },

    // KataGo's suggested next moves at the shown position, unless hidden.
    shownSuggestions() {
      const p = this.shownPosition;
      if(p === null || !this.suggestionsOn) return { moves: [], pass: false };
      return analysis.suggestions(this.game, p);
    },

    // Under the move line until the player first opens a line, while some shown suggestion has one.
    lineHintShown() {
      return !(this.$store.state.ui && this.$store.state.ui.lineHintSeen) && this.shownSuggestions.moves.some(s => s.line);
    },

    lineHeading() {
      const s = this.line.suggestion;
      const first = this.line.stoneChain.moves[this.line.from];
      const color = t(first && first.stone === Stone.WHITE ? 'color-white' : 'color-black');
      const lead = Math.abs(s.lead) < 0.5 ? t('chart-even')
        : t('chart-lead', { color: t(s.lead < 0 ? 'color-white' : 'color-black'), points: Math.abs(s.lead).toFixed(1) });
      return t('analysis-line', { rank: s.rank, color, point: s.move, n: this.line.total, lead });
    },

    // Waiting for the site's estimate of the position shown (plan 2026-10-29-katago-estimates.md).
    estimateWaiting() {
      if(this.estimating === null) return false;
      const p = this.estimatePosition();
      return p.moveIndex === this.estimating.moveIndex && p.variationMoves === this.estimating.variationMoves;
    },

    // Black's lead KataGo gives the shown estimate (one made from its ownership), or null.
    katagoEstimate() {
      const inf = this.state.infChain;
      return inf && inf.katago !== undefined && inf.index === (this.study ? this.study.start : this.cursor) ? inf.katago : null;
    },

    // The previous and next mistakes from the main-line position shown, or the one a line leaves
    // from or a view is of; null when the game has none, in 试下 and in practice.
    mistakeNav() {
      if(this.study || this.practice || this.showingVariationFrom !== null || !this.showWinrates || analysis.mistakes(this.game).length === 0) return null;
      const p = this.compare ? this.compare.c.p : this.line ? this.line.from - 1 : this.shownPosition;
      return p === null ? null : analysis.mistakesAround(this.game, p);
    },

    // Whether the shown stone is a mistake with the 实战 / 推荐 views.
    comparable() {
      return this.shownPosition !== null && analysis.comparison(this.game, this.shownPosition) !== null;
    },

    // Whether the views have territory maps (analyses since version 3): then also 对比.
    compareMaps() {
      const c = this.compare && this.compare.c;
      return Boolean(c && c.base && c.best.steps[0] && c.actual.steps[0].ownership);
    },

    compareHeading() {
      return this.moveLineAt(this.compare.c.p);
    },

    // What the view shows (#compare-actual, #compare-suggested, #compare-both).
    compareWhat() {
      const c = this.compare.c;
      if(this.compare.tab === 'both') {
        const s = c.played.stone === Stone.WHITE ? -1 : 1;
        return t('compare-both', { best: c.best.move, played: c.actual.move, points: Math.max(0, s * (c.best.lead - c.actual.lead)).toFixed(1) });
      }
      const v = this.compare.tab === 'actual' ? c.actual : c.best;
      const step = this.line && v.steps[this.line.n - 1];
      const lead = this.leadText(step ? step.scoreLead : v.lead);
      return t(this.compare.tab === 'actual' ? 'compare-actual' : 'compare-suggested', { point: v.move, n: this.line ? this.line.total : v.line.length, lead });
    },

    // The labels of the shown view (SPEC §6.6): stones KataGo expects to die or live, and in 对比
    // whether KataGo's move answered locally.
    compareLabels() {
      const c = this.compare && this.compare.c;
      if(!c) return [];
      if(this.compare.tab === 'both') {
        const last = this.game.moves[c.p - 1];
        const kind = last && last.stone && last.stone !== Stone.NONE && last.move && !last.move.startsWith('.')
          && analysis.answerLabel(this.game.decode(last.move), this.game.decode(c.actual.move), this.game.decode(c.best.move));
        return kind ? [t(kind === 'local' ? 'label-answer-local' : 'label-tenuki')] : [];
      }
      const v = this.compare.tab === 'actual' ? c.actual : c.best;
      const step = this.line && v.steps[this.line.n - 1];
      if(!step || !step.ownership) return [];
      return analysis.stoneLabels(this.compare.board, this.game.grid, analysis.decodeOwnership(c.base), analysis.decodeOwnership(step.ownership))
        .map(l => t(l.kind === 'dies' ? 'label-dies' : 'label-lives', { where: t(`region-${l.region}`), color: t(`color-${l.stone}`), n: l.n }));
    },

    // Practice is on, and not hidden behind a mistake's views.
    practising() {
      return Boolean(this.practice) && !this.compare;
    },

    // The mistakes 练习 would ask about (SPEC §6.7): the viewer's own, else both players'.
    practiceList() {
      if(!this.showWinrates) return [];
      const colour = this.player === Stone.BLACK || this.player === Stone.WHITE ? this.player : null;
      return analysis.practiceMistakes(this.game, colour);
    },

    canPractise() {
      return !this.study && !this.line && !this.compare && !this.practice && this.showingVariationFrom === null
        && this.practiceList.length > 0;
    },

    practiceItem() {
      return this.practice && !this.practice.done ? this.practice.list[this.practice.i] : null;
    },

    practiceHead() {
      const pr = this.practice;
      if(pr.done) return t('practise-done');
      const item = this.practiceItem;
      const k = pr.list.slice(0, pr.i + 1).filter(m => m.stone === item.stone).length;
      const params = { n: pr.i + 1, total: pr.list.length, k };
      return item.own ? t('practise-progress', params)
        : t('practise-progress-both', { ...params, color: t(item.stone === Stone.WHITE ? 'color-white' : 'color-black') });
    },

    practiceQuestion() {
      const item = this.practiceItem;
      const m = this.game.moves[item.index];
      const j = analysis.judge(this.game, item.index);
      const MARK = '\u0000';
      const [before, after] = t('practise-question', { n: item.index, color: t(m.stone === Stone.WHITE ? 'color-white' : 'color-black'),
                                                       point: m.move, points: MARK }).split(MARK);
      return { before, points: j ? j.lost.toFixed(1) : '', after };
    },

    practiceGood() {
      const g = this.practice && this.practice.guess;
      return Boolean(g && g.verdict && (g.verdict.kind === 'best' || g.verdict.kind === 'good'));
    },

    practiceVerdictText() {
      const { move, verdict: v } = this.practice.guess;
      if(!v) return '';
      const f = x => x.toFixed(1);
      switch(v.kind) {
        case 'same': return t('practise-same', { point: move });
        case 'best': return t('practise-best', { point: move });
        case 'good': return t('practise-good', { point: move, points: f(v.points) });
        case 'not-yet': return t('practise-not-yet', { point: move, better: f(v.better), worse: f(v.worse) });
        case 'worse': return t('practise-worse', { point: move, points: f(v.points) });
        case 'dismissed': return t('practise-dismissed', { point: move });
        default: return t('practise-unknown', { point: move });
      }
    },

    practiceHint() {
      const g = this.practice && this.practice.guess;
      const hint = g && g.verdict && g.verdict.hint;
      return hint ? t(hint === 'far' ? 'practise-hint-far' : 'practise-hint-near') : null;
    },

    practiceComparable() {
      return Boolean(this.practiceItem) && analysis.comparison(this.game, this.practiceItem.index) !== null;
    },

    practiceTally() {
      const r = this.practice.results;
      const found = r.filter(x => x && (x.kind === 'first' || x.kind === 'later')).length;
      const first = r.filter(x => x && x.kind === 'first').length;
      return t('practise-count', { total: this.practice.list.length, found, first });
    },

    practiceRows() {
      return this.practice.list.map((item, i) => {
        const r = this.practice.results[i] || { kind: 'skipped' };
        const params = { n: item.index, played: this.game.moves[item.index].move, point: r.point, k: r.tries };
        return t(`practise-row-${r.kind}`, params);
      });
    },

    lineAtStart() {
      return Boolean(this.line) && this.line.n <= 1;
    },

    lineAtEnd() {
      return Boolean(this.line) && this.line.n >= this.line.total;
    },

    // The line under the board for the shown move: text before and after the points (shown red for
    // a mistake), or null.
    moveLine() {
      const p = this.shownPosition;
      if(!p || !this.showWinrates) return null;
      const line = this.moveLineAt(p);
      if(line && this.shownSuggestions.pass) line.after += t('analysis-best-pass');
      return line;
    },
  },

  methods: {
    // The move line of stone p: text before and after the points (shown red for a mistake), or null.
    moveLineAt(p) {
      const m = this.game.moves[p];
      if(!m) return null;
      const color = t(m.stone === Stone.WHITE ? 'color-white' : 'color-black');
      const line = { before: '', points: null, after: '', mistake: false };
      if(m.move.startsWith('.')) {
        line.before = t('analysis-move-pass', { n: p, color });
      } else {
        const j = analysis.judge(this.game, p);
        const params = { n: p, color, point: m.move };
        if(!j) {
          line.before = t('analysis-move-none', params);
        } else if(j.best && j.lost < 0.5) {
          line.before = t('analysis-move-best', params);
        } else {
          const MARK = '\u0000';
          [line.before, line.after] = t('analysis-move-lost', { ...params, points: MARK }).split(MARK);
          line.points = j.lost.toFixed(1);
          line.mistake = j.lost >= analysis.MISTAKE_POINTS;
        }
      }
      return line;
    },

    // A lead as the chart says it: #chart-lead, or #chart-even under half a point.
    leadText(lead) {
      return Math.abs(lead) < 0.5 ? t('chart-even')
        : t('chart-lead', { color: t(lead < 0 ? 'color-white' : 'color-black'), points: Math.abs(lead).toFixed(1) });
    },

    // A chat message's move label: the board at that move, unless studying or in a view.
    showMove(n) {
      if(this.study || this.line || this.compare || this.practising) return;
      if(this.showingVariationFrom !== null) this.stopShowingVariation();
      this.setCursor(n + 1);
    },

    // To the previous (-1) or next (1) mistake: its stone shown. From a line it closes the line;
    // from a mistake's views it shows the other mistake's views, where it has them.
    jumpMistake(dir) {
      const index = this.mistakeNav && this.mistakeNav[dir < 0 ? 'prev' : 'next'];
      if(index === null || index === undefined) return;
      const views = Boolean(this.compare);
      if(this.compare) this.closeCompare();
      else if(this.line) this.closeLine();
      this.setCursor(index + 1);
      if(views && analysis.comparison(this.game, index)) this.openCompareAt(index);
    },

    // Opens the 实战 view of the shown mistake, at the move played.
    openCompare() {
      this.openCompareAt(this.shownPosition);
    },

    // Opens the 实战 view of mistake p.
    openCompareAt(p, tab = 'actual') {
      const c = analysis.comparison(this.game, p);
      if(!c) return;
      const g = this.game.clone();
      g.resetTo(c.p);
      this.compare = { c, tab, board: g.board };
      this.showCompareTab(tab);
    },

    // Shows one of the views: 实战 and 推荐 a line from the position before the mistake (numbered
    // from 1, stepped like a suggestion's line), 对比 that position with KataGo's move as its disc
    // and the move played ringed.
    showCompareTab(tab) {
      const c = this.compare.c;
      this.compare.tab = tab;
      this.state.infChain = null;
      this.state.markChain.clear();
      if(tab === 'both') {
        this.line = null;
        this.state.stoneChain = new ReplayStoneChain(this.game.moves, c.p);
        this.state.suggestions = [{ ...this.game.decode(c.best.move), rank: 1 }];
        this.state.actual = { ...this.game.decode(c.played.move), stone: c.played.stone };
        this.state.input.variations = null;
      } else {
        const v = tab === 'actual' ? c.actual : c.best;
        this.state.suggestions = [];
        this.state.actual = null;
        this.line = new LineView(this.game, c.p, { move: v.move, rank: 1, lead: v.lead, line: v.line }, []);
        this.state.stoneChain = this.line.stoneChain;
        this.setCursor(c.p + 1);
      }
      this.compareSquares();
    },

    // The shown view's territory map (SPEC §6.6): in 实战 and 推荐, what the line up to the shown
    // move brings against the position before the mistake (black / white: who gains), none on the
    // line's stones; in 对比, what only KataGo's move gets (blue) and only the move played (red).
    compareSquares() {
      const c = this.compare.c;
      const own = analysis.decodeOwnership;
      if(this.compare.tab === 'both') {
        const black = c.played.stone !== Stone.WHITE;
        const skip = [this.game.decode(c.best.move), this.game.decode(c.played.move)];
        this.state.squares = c.best.steps[0] && analysis.changedPoints(this.game.grid, own(c.actual.steps[0].ownership),
          own(c.best.steps[0].ownership), black ? ['blue', 'red'] : ['red', 'blue'], skip);
        return;
      }
      const v = this.compare.tab === 'actual' ? c.actual : c.best;
      const n = this.line.n;
      const step = v.steps[n - 1];
      const skip = v.line.slice(0, n).map(m => this.game.decode(m));
      this.state.squares = step ? analysis.changedPoints(this.game.grid, own(c.base), own(step.ownership), ['black', 'white'], skip) : null;
    },

    closeCompare() {
      const p = this.compare.c.p;
      this.state.squares = null;
      this.state.infChain = null;
      this.line = null;
      this.compare = null;
      if(this.practice) {
        this.showQuestion();
        return;
      }
      this.state.stoneChain = new ReplayStoneChain(this.game.moves, p + 1);
      this.moveCursor(0);
      this.applySuggestions();
    },

    // ---- Practice (SPEC §6.7)

    startPractice() {
      const from = this.practice ? this.practice.from : this.cursor;
      this.state.infChain = null;
      this.state.markChain.clear();
      this.practice = { list: this.practiceList.slice(), i: 0, results: [], tries: 0, guess: null, answered: false,
                        done: false, game: null, from };
      this.showQuestion();
    },

    // The board of the current question: the position before the mistake, the stone played
    // ringed; a guess, if any, is taken back.
    showQuestion() {
      const pr = this.practice;
      const item = pr.list[pr.i];
      const m = this.game.moves[item.index];
      pr.game = this.game.clone();
      pr.game.resetTo(item.index);
      pr.guess = null;
      this.selected = null;
      this.state.infChain = null;
      this.state.squares = null;
      this.state.stoneChain = new ReplayStoneChain(this.game.moves, item.index);
      this.state.input = new PracticeInput(pr);
      this.state.suggestions = [];
      this.state.actual = { ...this.game.decode(m.move), stone: m.stone };
    },

    onActionPractice(action) {
      if(this.practice.done || this.practice.guess) return;
      switch(action.type) {
        case Action.Type.PUT:
          if(action.point) this.practiseGuess(action.point);
          break;
        case Action.Type.SELECT:
          this.selected = action.point;
          break;
      }
    },

    // Plays the guess at point and judges it (SPEC §6.7).
    practiseGuess(point) {
      const pr = this.practice;
      if(!point || pr.guess || !pr.game.canPlay(point)) return;
      const item = pr.list[pr.i];
      const move = this.game.encode(point.x, point.y);
      const g = pr.game.clone();
      g.play(point);
      this.state.stoneChain = new ReplayStoneChain(g.moves, g.moves.length);
      // The ring would cover a guess on the point actually played.
      if(move === this.game.moves[item.index].move) this.state.actual = null;
      pr.tries++;
      const verdict = analysis.practiceVerdict(this.game, item.index, move);
      pr.guess = { move, verdict };
      const open = !pr.results[pr.i] || pr.results[pr.i].kind === 'skipped';
      if(open && verdict && (verdict.kind === 'best' || verdict.kind === 'good'))
        this.$set(pr.results, pr.i, { kind: pr.answered ? 'shown' : pr.tries === 1 ? 'first' : 'later', tries: pr.tries, point: move });
    },

    practiseAgain() {
      this.showQuestion();
    },

    // The mistake's views; their back button comes back to the question.
    practiseAnswer() {
      const pr = this.practice;
      if(!pr.results[pr.i] || pr.results[pr.i].kind === 'skipped') this.$set(pr.results, pr.i, { kind: 'shown' });
      pr.answered = true;
      this.state.actual = null;
      this.state.input = new VariationSelectorInputValidator(this.game);
      // The answer first: KataGo's move and its line.
      this.openCompareAt(pr.list[pr.i].index, 'suggested');
    },

    // Back to the previous question; its result so far is kept (a skipped one can still be found).
    practisePrev() {
      const pr = this.practice;
      pr.i--;
      pr.tries = 0;
      pr.answered = pr.results[pr.i] && pr.results[pr.i].kind === 'shown';
      this.showQuestion();
    },

    // The next question, also from the mistake's views.
    practiseNext() {
      const pr = this.practice;
      if(this.compare) {
        this.state.squares = null;
        this.state.infChain = null;
        this.line = null;
        this.compare = null;
      }
      if(!pr.results[pr.i]) this.$set(pr.results, pr.i, { kind: 'skipped' });
      pr.i++;
      pr.tries = 0;
      pr.answered = false;
      if(pr.i < pr.list.length) {
        this.showQuestion();
        return;
      }
      pr.done = true;
      pr.guess = null;
      this.state.actual = null;
      this.state.input = new VariationSelectorInputValidator(this.game);
      this.state.stoneChain = new ReplayStoneChain(this.game.moves, this.game.moves.length);
    },

    endPractice() {
      const from = this.practice.from;
      this.practice = null;
      this.selected = null;
      this.state.squares = null;
      this.state.actual = null;
      this.state.input = new VariationSelectorInputValidator(this.game);
      this.state.stoneChain = new ReplayStoneChain(this.game.moves, from);
      this.moveCursor(0);
      this.applySuggestions();
    },

    t,

    // Puts the shown suggestions on the board, with the stone actually played next (SPEC §6.6).
    applySuggestions() {
      if(this.compare || this.practice) return;  // a mistake's views put their own marks (showCompareTab)
      this.state.suggestions = this.shownSuggestions.moves.map(s => ({ ...this.game.decode(s.move), rank: s.rank }));
      const p = this.shownPosition;
      const next = this.state.suggestions.length === 0 ? null
        : this.game.moves.find(m => m.index === p + 1 && m.move && !m.move.startsWith('.'));
      this.state.actual = next ? { ...this.game.decode(next.move), stone: next.stone } : null;
    },

    switchChartLine() {
      this.$store.commit('set', { ui: { chartLine: this.chartLine === 'lead' ? 'winrate' : 'lead' } });
      localStorage.setItem('ui', JSON.stringify(this.$store.state.ui));
    },

    // Whether practice's texts overflow the chart's place on a PC (then … opens them).
    // Lines that do not fit whole are hidden, not cut through the middle.
    measurePractice() {
      const box = this.$refs.practiceBox;
      if(!box) return;
      const lines = box.querySelectorAll('.practice > *, .compare > *');
      const cut = this.side && !this.practiceOpen && box.scrollHeight > box.clientHeight + 1;
      for(const l of lines) l.classList.toggle('cut', cut && l.offsetTop + l.offsetHeight > box.clientHeight + 1);
      if(this.practiceOpen) return;
      if(cut !== this.practiceCut) this.practiceCut = cut;
    },

    switchSuggestions() {
      this.$store.commit('set', { ui: { suggestions: this.suggestionsOn ? 'hide' : 'show' } });
      localStorage.setItem('ui', JSON.stringify(this.$store.state.ui));
    },

    async requestAnalysis() {
      this.requesting = true;
      try {
        const r = await api.post(`${api.GAMES}/${this.game.id}/analysis-request`, {});
        this.pollProgress = Date.now();
        this.$set(this.game, 'analysisRequest', r.data || { time: new Date() });
      } catch(err) {
        const reason = err instanceof RestError ? errorText(err.msg) : String(err);
        this.$emit('message', t('analysis-request-failed', { reason }), 'warn');
      } finally {
        this.requesting = false;
      }
    },

    // While the analysis is incomplete (requested, or with gaps such as the last positions of a game
    // just finished), reads the game again now and then and adds the analyses that arrived, without
    // disturbing a study or a variation being viewed.
    pollAnalysis() {
      if(this.pollTimer || this.destroyed) return;
      if(!(this.game.analysisRequest || analysis.any(this.game)) || analysis.complete(this.game)) return;
      if(!this.pollProgress) this.pollProgress = Date.now();
      if(Date.now() - this.pollProgress > POLL_GIVE_UP_MS) return;
      this.pollTimer = setTimeout(async () => {
        try {
          const data = await api.get(api.GAMES, this.game.id);
          for(const d of data.moves || []) {
            const m = this.game.moves[d.index];
            if(m && m.move === d.move && d.analysis && !(m.analysis && m.analysis.engine)) {
              this.$set(m, 'analysis', d.analysis);
              this.pollProgress = Date.now();
            }
          }
        } catch(err) {
          // Tried again at the next round.
        }
        this.pollTimer = null;
        this.pollAnalysis();
      }, POLL_MS);
    },

    onAction(action) {
      if(this.line || this.compare)
        return;  // the board takes no taps while a line or a mistake's views are shown
      if(this.practice)
        this.onActionPractice(action);
      else if(this.study)
        this.onActionStudy(action);
      else
        this.onActionReplay(action);
    },

    onActionReplay(action) {
      switch(action.type) {
        case Action.Type.PUT:
          if(!this.selectVariation(action.point) && !this.openLineAt(action.point || action.at))
            this.moveCursor(1);
          break;

        case Action.Type.SWIPE_RIGHT:
          if(!this.selectVariation(action.point))
            this.moveCursor(1);
          break;

        // In button mode at a position with saved variations: a suggestion's line opens at once.
        case Action.Type.SELECT:
          this.openLineAt(action.point);
          break;

        case Action.Type.UNPUT:
        case Action.Type.SWIPE_LEFT:
          this.moveCursor(-1);
          break;

        case Action.Type.WHEEL:
          if(action.point.y !== 0)
            this.moveCursor(action.point.y);
          break;
      }
    },

    onActionStudy(action) {
      switch(action.type) {
        case Action.Type.PUT:
          if(action.point) this.studyPut(action.point);
          break;

        case Action.Type.UNPUT:
          if(!this.study.thinking) this.moveCursor(-1);
          break;

        case Action.Type.SELECT:
          if(this.study.thinking) break;
          this.state.infChain = null;
          this.selected = action.point;
          break;
      }
    },

    selectVariation(point) {
      if(!point || !this.state.input.variations)
        return false;

      for(const v of this.state.input.variations) {
        if(v.moves[0].move === this.game.encode(point.x, point.y)) {
          const varGame = this.game.clone();
          varGame.resetTo(this.cursor);
          varGame.moves = varGame.moves.concat(v.moves);
          this.showingVariationFrom = this.cursor;
          this.varDesc = v.description;
          this.state.stoneChain = new ReplayStoneChain(varGame.moves, this.cursor);
          this.moveCursor(1);
          return true;
        }
      }
      return false;
    },

    // Opens the line of the shown suggestion at point, if it has one.
    openLineAt(point) {
      if(!point) return false;
      const m = this.game.encode(point.x, point.y);
      const s = this.shownSuggestions.moves.find(x => x.move === m && x.line);
      if(!s) return false;
      this.openLine(s, this.shownSuggestions.moves, this.cursor);
      if(!(this.$store.state.ui && this.$store.state.ui.lineHintSeen)) {
        this.$store.commit('set', { ui: { lineHintSeen: true } });
        localStorage.setItem('ui', JSON.stringify(this.$store.state.ui));
      }
      return true;
    },

    openLine(suggestion, shown, from) {
      this.state.infChain = null;
      this.state.suggestions = [];
      this.state.actual = null;
      this.line = new LineView(this.game, from, suggestion, shown);
      this.state.stoneChain = this.line.stoneChain;
      this.setCursor(from + 1);
    },

    // The shown suggestion with a line before (-1) or after (1) the one whose line is shown.
    neighbourLine(dir) {
      const list = this.line.shown;
      for(let i = list.indexOf(this.line.suggestion) + dir; i >= 0 && i < list.length; i += dir) {
        if(list[i].line) return list[i];
      }
      return null;
    },

    switchLine(dir) {
      const s = this.neighbourLine(dir);
      if(s) this.openLine(s, this.line.shown, this.line.from);
    },

    closeLine() {
      this.state.infChain = null;
      this.state.stoneChain = new ReplayStoneChain(this.game.moves, this.line.from);
      this.line = null;
      this.moveCursor(0);
      this.applySuggestions();
    },

    // A tap on the chart goes to that position of the game, also from a line.
    chartCursor(n) {
      if(this.compare) this.closeCompare();
      else if(this.line) this.closeLine();
      this.setCursor(n);
    },

    // The first icon: the line's first move, else the start of the main line or of the variation.
    reset() {
      if(this.line) this.setCursor(this.line.from + 1);
      else if(this.showingVariationFrom === null) this.setCursor(1);
      else this.stopShowingVariation();
    },

    stopShowingVariation() {
      this.state.stoneChain = new ReplayStoneChain(this.game.moves, this.showingVariationFrom);
      this.showingVariationFrom = null;
      this.varDesc = null;
      this.moveCursor(0);
    },

    buttonPut() {
      if(this.study) this.studyPut(this.selected);
      else if(this.practice) this.practiseGuess(this.selected);
      this.selected = null;
    },

    setCursor(newCursor) {
      if(this.study) {
        if(newCursor >= 1) {
          const n = this.study.start + newCursor - 1;
          if(n < this.study.expGame.moves.length) {
            this.study.expGame.resetTo(n);
            this.aiTakenBack();
          }
        }
      } else {
        const MAX = this.state.stoneChain.moves.length;
        let n = newCursor;

        if(n < 1 || n > MAX)
          n = MAX;

        this.state.stoneChain.cursor = n;
        this.state.infChain = null;
        this.state.markChain.clear();
        if(this.compare && this.line) this.compareSquares();

        const variations = this.state.stoneChain.moves[n - 1].variations;
        this.state.input.variations = variations;
        this.state.input.nextMove = this.state.stoneChain.moves[n];
        if(variations) {
          const actualMove = this.state.stoneChain.moves[n];
          if(actualMove) this.state.markChain.mark('A', actualMove.move);

          const MARKS="BCDEFGHIJKLMNOPQRSTUVWXYZ";
          for(let i = 0; i < variations.length; i++) {
            let mark = MARKS[i];
            if(!mark) mark = '?';

            const move = variations[i].moves[0];
            if(move) this.state.markChain.mark(mark, move.move);
          }
        }
      }
    },

    moveCursor(delta) {
      if(this.study) {
        let n = this.study.expGame.moves.length - this.study.start + 1 + delta;
        if(n < 1) n = 1;
        this.setCursor(n);
      } else if(this.line) {
        const n = Math.max(this.line.from + 1, Math.min(this.cursor + delta, this.state.stoneChain.moves.length));
        this.setCursor(n);
      } else {
        const MIN = this.showingVariationFrom === null ? 1 : this.showingVariationFrom;
        const MAX = this.state.stoneChain.moves.length;
        const OVERFLOW = (this.showingVariationFrom === null);

        let n = this.cursor + delta;
        if(n < MIN) {
          if(this.cursor === MIN && OVERFLOW)
            n = MAX;
          else
            n = MIN;
        } else if(n > MAX) {
          if(this.cursor === MAX && OVERFLOW)
            n = MIN;
          else
            n = MAX;
        }

        if(n === MIN && this.showingVariationFrom)
          this.stopShowingVariation();
        else
          this.setCursor(n);
      }
    },

    // The position an estimate is asked for, as the site echoes it: { moveIndex, variationMoves }
    // (a count of stones); the same key while the board shows the same position.
    estimatePosition() {
      if(this.study) return { moveIndex: this.study.mainStart, variationMoves: this.study.expGame.moves.length - this.study.mainStart };
      if(this.varFrom !== null) return { moveIndex: this.varFrom, variationMoves: this.cursor - this.varFrom };
      return { moveIndex: this.cursor, variationMoves: 0 };
    },

    // Shows the estimate of exactly what is shown (SPEC §6.3): an analysed main-line position's from
    // the stored ownership, else the site's, KataGo's when it could reach it, else Pachi's. A second
    // tap hides it, or stops waiting for it.
    async estimate() {
      if(this.state.infChain !== null || this.estimating !== null) {
        this.state.infChain = null;
        this.estimating = null;
      } else if(!this.study && this.varFrom === null && analysis.ownershipPointMap(this.game, this.cursor - 1)) {
        // An analysed main-line position: KataGo's estimate, from its ownership (SPEC §6.3).
        const chain = new InfluenceChain(this.game, this.cursor, analysis.ownershipPointMap(this.game, this.cursor - 1));
        chain.katago = analysis.analysisAt(this.game, this.cursor - 1).scoreLead || 0;
        this.state.infChain = chain;
      } else {
        const est = { game: this.game.id, };

        if(this.study) {
          est.moveIndex = this.study.mainStart;
          est.variationMoves = this.study.expGame.moves.slice(this.study.mainStart).map(m => Session.convertMove(m));
        } else if(this.varFrom !== null) {
          est.moveIndex = this.varFrom;
          est.variationMoves = this.state.stoneChain.moves.slice(this.varFrom, this.cursor).map(m => Session.convertMove(m));
        } else {
          est.moveIndex = this.cursor;
        }

        const asked = this.estimating = this.estimatePosition();
        let r;
        try {
          r = await api.post(api.ESTIMATES, est);
        } finally {
          if(this.estimating !== asked) r = null;
          else this.estimating = null;
        }
        if(!r) return;

        const now = this.estimatePosition();
        const infMoveIndex = r.data.moveIndex;
        const infVarMoves = r.data.variationMoves ? r.data.variationMoves.length : 0;

        if(infMoveIndex === now.moveIndex && infVarMoves === now.variationMoves) {
          const chain = new InfluenceChain(this.game, this.study ? this.study.start : this.cursor, r.data.countResult.pointMap);
          if(r.data.engine === 'katago') chain.katago = r.data.katago.scoreLead;
          this.state.infChain = chain;
        }
      }
    },

    // Starts a study at the position the board shows (SPEC §6.4): the main line, a suggestion's
    // line or a mistake's view at the shown step, or practice's question with the judged guess as
    // its first stone. 结束试下 comes back to that view as it was.
    startStudy() {
      const back = {
        line: this.line, compare: this.compare, practice: this.practice,
        stoneChain: this.state.stoneChain, input: this.state.input, suggestions: this.state.suggestions,
        actual: this.state.actual, squares: this.state.squares,
      };
      let study, guess = null;
      if(this.practice && !this.compare) {
        const item = this.practice.list[this.practice.i];
        guess = this.practice.guess && this.game.decode(this.practice.guess.move);
        study = new Study(this.game, item.index, { back });
      } else if(this.line) {
        const c = this.compare && this.compare.c;
        const tab = c && this.compare.tab === 'actual'
          ? t('compare-tab-actual', { point: c.actual.move })
          : t('compare-tab-suggested', { point: c ? c.best.move : this.line.suggestion.move });
        study = new Study(this.game, this.cursor, { base: this.line.lineGame, mainStart: this.line.from, back,
                                                    from: t('study-from-line', { line: tab, n: this.line.n }) });
      } else {
        study = new Study(this.game, this.cursor);
      }
      if(this.aiOffered) study.ai = this.newAi(study);

      this.line = null;
      this.compare = null;
      this.practice = null;
      this.state.infChain = null;
      this.state.squares = null;
      this.state.suggestions = [];
      this.state.actual = null;
      this.state.markChain.clear();
      this.selected = null;
      this.aiAnswers = new Map();
      this.study = study;
      this.state.stoneChain = study.stoneChain;
      this.state.input = study.input;
      // The guess is the study's first stone, so the AI answers it at once.
      if(guess) this.studyPut(guess);
    },

    stopStudy() {
      const back = this.study.back;
      this.aiToken = (this.aiToken || 0) + 1;
      this.state.infChain = null;
      this.selected = null;
      if(back) {
        this.study = null;
        this.line = back.line;
        this.compare = back.compare;
        this.practice = back.practice;
        Object.assign(this.state, { stoneChain: back.stoneChain, input: back.input, suggestions: back.suggestions,
                                    actual: back.actual, squares: back.squares });
        this.state.markChain.clear();
        if(this.line) this.setCursor(this.cursor);
        return;
      }
      this.state.input = new VariationSelectorInputValidator(this.game);
      this.state.stoneChain = new ReplayStoneChain(this.game.moves, this.study.start);
      this.study = null;
      this.moveCursor(0);
    },

    toggleStudy() {
      if(!this.study)
        this.startStudy();
      else
        this.stopStudy();
    },

    // ---- The AI in 试下 (plan 2026-10-27-katago-replies.md)

    // At the start the colour to move is by hand and the other automatic; my colour is the
    // viewer's in the game, else the colour to move.
    newAi(study) {
      const toMove = study.expGame.nextPlayer;
      const mine = this.player === Stone.BLACK || this.player === Stone.WHITE ? this.player : toMove;
      return {
        mine,
        auto: { black: toMove !== Stone.BLACK, white: toMove !== Stone.WHITE },
        // The colour whose move the AI is working out, or null.
        thinking: null,
        // The AI's moves since the last stone by hand, tap on the control or switch, and whether it
        // paused at the limit.
        run: 0,
        paused: false,
        // ① the last stone by hand, ② the AI's last stone, and the labels; a failure.
        judged: null,
        reply: null,
        labels: [],
        error: null,
      };
    },

    // A stone placed by hand; then the AI plays the next colour if it is automatic.
    studyPut(point) {
      const st = this.study;
      if(!point || st.thinking) return;
      this.state.infChain = null;
      if(!st.put(point)) return;
      if(!st.ai) return;
      Object.assign(st.ai, { run: 0, paused: false, judged: null, reply: null, labels: [], error: null });
      if(st.ai.auto[this.colorKey(st.expGame.nextPlayer)]) this.askAi();
    },

    colorKey(stone) {
      return stone === Stone.WHITE ? 'white' : 'black';
    },

    colorName(stone) {
      return t(stone === Stone.WHITE ? 'color-white' : 'color-black');
    },

    // A stone taken back: nothing is played automatically, the colour now to move goes to 手动 (the
    // player wants to try that move again by hand), and the lines about the exchange go.
    aiTakenBack() {
      const ai = this.study && this.study.ai;
      if(!ai) return;
      ai.auto[this.colorKey(this.study.expGame.nextPlayer)] = false;
      Object.assign(ai, { run: 0, paused: false, judged: null, reply: null, labels: [], error: null });
    },

    // A colour's switch: on at that colour's turn, the AI plays it; off drops its answer.
    toggleAuto(stone) {
      const st = this.study, ai = st.ai, key = this.colorKey(stone);
      ai.auto[key] = !ai.auto[key];
      ai.paused = false;
      ai.run = 0;
      if(!ai.auto[key] && ai.thinking === stone) {
        this.aiToken = (this.aiToken || 0) + 1;
        ai.thinking = null;
      } else if(ai.auto[key] && !ai.thinking && st.expGame.nextPlayer === stone) {
        this.askAi();
      }
    },

    // AI 下, 继续 and 再试一次: the AI plays the colour to move. A tap starts the count of moves in a
    // row again, as a stone by hand does.
    aiPlay() {
      const ai = this.study.ai;
      ai.run = 0;
      ai.paused = false;
      this.askAi();
    },

    // Asks the site for the AI's move at the study's position, waiting for it (POST
    // /api/ai/searches?until=done); an answer this study already had is used again.
    async askAi() {
      const st = this.study, ai = st.ai;
      const color = st.expGame.nextPlayer;
      const token = this.aiToken = (this.aiToken || 0) + 1;
      const moves = st.variationMoves();
      const key = `${st.mainStart}:` + moves.map(m => m.move + (m.by ? '*' : '')).join(',');
      ai.thinking = color;
      ai.error = null;
      let result = this.aiAnswers.get(key) || null, error = null;
      if(!result) {
        try {
          const s = await api.call('POST', 'ai/searches?until=done&wait=20',
                                   { game: this.game.id, moveIndex: st.mainStart, variationMoves: moves });
          const r = s.results && s.results[s.results.length - 1];
          if(s.state === 'done' && r && r.after) result = r;
          else if(s.state === 'failed') error = { tag: 'ai-failed', reason: (r && r.error) || '' };
          else error = { tag: 'ai-timeout', retry: true };
        } catch(err) {
          const code = err instanceof RestError ? err.msg : null;
          if(code === 'ai-offline') error = { tag: 'ai-offline', hint: 'ai-offline-hint' };
          else if(code === 'ai-busy') error = { tag: 'ai-busy', retry: true };
          else error = { tag: 'ai-failed', reason: code ? errorText(code) : String(err) };
        }
        if(result) this.aiAnswers.set(key, result);
      }
      if(token !== this.aiToken || this.study !== st) return;  // taken back, switched off or ended meanwhile
      ai.thinking = null;
      if(error) {
        ai.error = error;
        ai.run = 0;
        if(error.tag === 'ai-offline') ai.auto = { black: false, white: false };
        return;
      }
      this.aiMoved(result, color);
    },

    // Plays the AI's move and says how the stone by hand before it fared (SPEC §6.4).
    aiMoved(r, color) {
      const st = this.study, ai = st.ai, g = st.expGame;
      const last = g.lastMove;
      ai.judged = null;
      ai.labels = [];
      if(r.before && last && last.index >= st.start && !last.by && !last.move.startsWith('.')) {
        const s = last.stone === Stone.WHITE ? -1 : 1;
        const lost = Math.max(0, s * (r.before.scoreLead - r.after.scoreLead));
        const params = { n: st.number(last), color: this.colorName(last.stone), point: last.move };
        if(r.before.move === last.move) ai.judged = { kind: 'best', text: t('study-ai-move-best', params) };
        else if(lost <= analysis.PRACTICE_GOOD) ai.judged = { kind: 'good', text: t('study-ai-move-good', { ...params, points: lost.toFixed(1) }) };
        else {
          const MARK = '\u0000';
          const [before, after] = t('study-ai-move-lost', { ...params, best: r.before.move, points: MARK }).split(MARK);
          ai.judged = { kind: 'lost', before, points: lost.toFixed(1), after, mistake: lost >= analysis.MISTAKE_POINTS };
        }
        const own = analysis.decodeOwnership;
        ai.labels = analysis.stoneLabels(g.board, this.game.grid, own(r.before.ownership), own(r.after.ownership))
          .map(l => t(l.kind === 'dies' ? 'label-dies' : 'label-lives', { where: t(`region-${l.region}`), color: t(`color-${l.stone}`), n: l.n }));
      }

      const played = r.after.move === 'pass' ? g.pass() : g.play(this.game.decode(r.after.move));
      if(!played) {
        ai.error = { tag: 'ai-failed', reason: r.after.move };
        return;
      }
      g.lastMove.by = 'ai';
      const lead = this.leadText(r.after.scoreLead);
      ai.reply = r.after.move === 'pass'
        ? t('study-ai-reply-pass', { n: st.number(g.lastMove), lead })
        : t('study-ai-reply', { n: st.number(g.lastMove), color: this.colorName(color), point: r.after.move, lead });

      // Both automatic: on, up to the site's limit, then 继续.
      ai.run++;
      if(ai.auto[this.colorKey(g.nextPlayer)]) {
        if(ai.run >= this.aiAutoMoves) ai.paused = true;
        else this.askAi();
      }
    },

    saveVariation() {
      this.savingVariation = true;
    },

    cancelSaveVariation() {
      this.savingVariation = false;
      this.varDesc = null;
    },

    async postVariation() {
      const v = this.study && this.study.getVariation(`第${this.study.mainStart}手变化`, this.varDesc);
      if(v === null) {
        this.$emit('message', '请先试下', 'warn');
        return;
      }

      this.postingVariation = true;
      try {
        await api.post(api.VARIATIONS_OF(this.game.id, this.study.mainStart - 1), v);
        this.savingVariation = false;
        this.varDesc = null;
        this.stopStudy();
        this.$emit('message', `${v.name}保存成功`, 'info');
        this.$emit('reload');
      } catch(err) {
        let msg = '';
        if(err instanceof RestError)
          msg  = `保存失败: ${err.uiMsg}`;
        else
          msg  = `保存失败: ${err}`;

        this.$emit('message', msg, 'warn');
      } finally {
        this.postingVariation = false;
      }
    },
  },
}
</script>

<style scoped>
/* One order everywhere (SPEC §6.1, plan 2026-10-28-replay-layout.md): board, chart, icons and
   position, the mode row, then the info lines and the action row. Rows of buttons are grids of
   equal cells, so a button keeps its size and place when its neighbours change. */
.panel {
  margin: 0 auto;
}

.placement {
  margin: 10px auto;
  max-width: 300px;
}

.put {
  width: 300px;
  max-width: 100%;
}

/* The chart's place: the chart (80 px, 100 on a PC), or a note of that height; its switches are in
   the position row. */
.chart-slot {
  height: 80px;
  margin: 8px 0 8px;
}

.replay.desktop .chart-slot {
  height: 100px;
}

.chart-slot .winrate-chart {
  margin: 0;
}

.chart-slot.empty {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  border: 1px dashed #d8c9a8;
  border-radius: 6px;
  background: #fdf9f1;
}

.replay.desktop .chart-slot.empty {
  height: 100px;
}

.nav-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

/* The position, and on its right the chart's switches: one row under the icons. */
.position-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 360px;
  margin: 4px auto 0;
  height: 22px;
  color: #999;
  font-size: 13px;
  white-space: nowrap;
}

.position-row .chart-switches {
  margin-left: auto;
}

.position-row:has(.chart-switches) .position {
  margin-left: 2px;
}

.position-row .separator {
  color: #ccc;
}

/* First, back 5, back 1, forward 1, forward 5, last: one bar of icons. */
.replay-bar {
  display: flex;
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  box-sizing: border-box;
  border: 1px solid #c9b48a;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.replay-bar button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  height: 44px;
  padding: 0;
  border: none;
  border-left: 1px solid #eadfc6;
  background: #fff;
  color: #2c3e50;
  font-size: 13px;
  cursor: pointer;
}

.replay-bar button:first-child {
  border-left: none;
}

.replay-bar button:disabled {
  color: #ccc;
  cursor: default;
}

.replay-bar svg {
  width: 18px;
  height: 18px;
  fill: currentColor;
}

.move-hint {
  margin: 2px auto 0;
  color: #888;
  font-size: 13px;
}

/* The mode row and the action row: four cells of one size. */
.mode-row, .action-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
  align-items: center;
  justify-items: stretch;
  width: 100%;
  max-width: 360px;
  margin: 8px auto 2px;
}

.mode-row button, .action-row button.btn-secondary, .action-row button.btn-main {
  width: 100%;
  min-width: 0;
  height: 38px;
  padding: 0 2px;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
}

/* As tall as the 聊天 button, also where there is none (practice), so nothing under it moves. */
.action-row {
  margin-top: 10px;
  min-height: 52px;
}

.action-row .chat-btn {
  justify-self: center;
}

@media (max-width: 375px) {
  .mode-row, .action-row { gap: 4px; }
  .mode-row button, .action-row button.btn-secondary { font-size: 13px; }
}

/* What the board shows: at least two lines, so the action row under it seldom moves (not in
   practice, whose texts are above its buttons). */
.info {
  margin-top: 6px;
}

.info.reserve {
  min-height: 44px;
}

.line-switch {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 6px auto 0;
  color: #595959;
  font-size: 14px;
  white-space: nowrap;
}

.line-switch button {
  width: 34px;
  height: 30px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.line-switch svg {
  width: 14px;
  height: 14px;
  fill: currentColor;
}

.estimate-count {
  margin-top: 6px;
  color: #595959;
  font-size: 14px;
}

/* On a PC: the board as large as the window allows on the left, and beside it one column with
   everything else in the phone's order, except that the action row comes straight after the mode
   row, so the texts under it (which vary in length) never move a button (SPEC §6.1). */
.replay.side {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 28px;
  margin-top: 8px;
  padding: 0 16px;
}

.replay.side .board {
  flex: none;
}

.replay.side .panel {
  flex: 0 1 460px;
  min-width: 380px;
  margin: 0;
  display: flex;
  flex-direction: column;
}

.replay.side .panel > * { order: 10; }
.replay.side .chart-slot { order: 1; margin-top: 0; }
.replay.side .practice-info { order: 1; }
.replay.side .nav-row { order: 3; }
.replay.side .action-row { order: 4; }
.replay.side .placement { order: 5; }

.replay.side .replay-bar, .replay.side .position-row, .replay.side .mode-row, .replay.side .ai-control, .replay.side .action-row {
  max-width: none;
}

.replay.side .action-row {
  margin: 8px 0 6px;
}

.replay.side .info {
  min-height: 0;
}

/* Practice on a phone: its buttons right under the board, then (in 看答案) the icons, then its texts,
   so no button moves when 看答案 opens. No action row: 试下 sits with the texts. */
.practice-info {
  margin: 4px 0 2px;
}

.replay.practice:not(.side) .board {
  margin-bottom: 10px;
}

.replay.practice:not(.side) .mode-row {
  order: -1;
  margin-top: 0;
}

.replay.practice:not(.side) .nav-col {
  margin-top: 8px;
}

button.verdict-study {
  width: auto;
  height: 30px;
  margin-left: 10px;
  padding: 0 14px;
  font-size: 14px;
  font-weight: normal;
  vertical-align: 1px;
}

/* Practice on a PC: its texts in the chart's place (the same height), so its buttons sit where the
   replay's are; too long, they end in … , which shows them all over the buttons. */
.replay.side .practice-info {
  position: relative;
  height: 100px;
  margin: 0 0 8px;
}

.replay.side .practice-box {
  height: 100%;
  overflow: hidden;
}

.replay.side .practice-info.open {
  z-index: 5;
}

.replay.side .practice-info.open .practice-box {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: auto;
  min-height: 100%;
  padding-bottom: 8px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 14px #0000002e;
}

.replay.side .practice-info .practice-head,
.replay.side .practice-info .compare ::v-deep .move-line:first-child {
  margin-top: 0;
}

.practice-info ::v-deep .cut {
  visibility: hidden;
}

button.practice-more {
  position: absolute;
  right: 0;
  bottom: 0;
  width: auto;
  height: 22px;
  padding: 0 10px;
  border: 1px solid #c9b48a;
  border-radius: 11px;
  background: #fff;
  color: #595959;
  font-size: 14px;
  line-height: 18px;
  cursor: pointer;
}

.move-line {
  margin: 6px auto 0;
  color: #595959;
  font-size: 14px;
}

.move-line .mistake {
  color: #f00;
}

.analysis-none {
  margin: 10px auto;
  color: #999;
  font-size: 14px;
}

.analysis-none button.analysis-request {
  height: 36px;
  margin-left: 8px;
  padding: 0 12px;
  font-size: 14px;
}

/* The play mode switch (a button) keeps its own size, not the replay buttons' 80 px. */
button.play-mode {
  width: auto;
  height: auto;
}

.compare-label, .estimate-katago {
  margin: 2px auto 0;
  color: #595959;
  font-size: 14px;
}


/* The AI in 试下 (SPEC §6.4): one box for both colours, black and white only; the half of the
   colour to move wider, with the middle item in the middle of the box. */
.study-from {
  margin: 0 auto 4px;
  color: #888;
  font-size: 14px;
}

.study-ai-line {
  margin: 4px auto 2px;
  color: #2c3e50;
  font-size: 16px;
  font-weight: bold;
}

.study-ai-line .mistake { color: #f00; }
.study-ai-judged.good { color: #1a7f37; }
.study-ai-error { color: #b35c00; }

.study-ai-error button.ai-retry {
  height: 30px;
  margin-left: 8px;
  padding: 0 12px;
  font-size: 14px;
  font-weight: normal;
}

/* The AI control (SPEC §6.4): one box, my colour's half first; the half of the colour to move is
   wider, its edge halfway between the middle item and the other half's switch, and slides there
   when the turn changes. The switch knobs are the stones' colours. */
.ai-control {
  width: 100%;
  max-width: 360px;
  margin: 8px auto 2px;
}

.ai-box {
  position: relative;
  display: flex;
  height: 38px;
  box-sizing: border-box;
  border: 1px solid #5a5e63;
  border-radius: 10px;
  overflow: hidden;
}

/* Middle item 112 px, switch 72 px, padding 5 px: the narrow half ends at a quarter of the box
   plus (5 + 72 - 56) / 2 px. */
.ai-half {
  flex: none;
  width: calc(25% + 10.5px);
  display: flex;
  align-items: center;
  padding: 0 5px;
  box-sizing: border-box;
  transition: width .3s ease;
}

.ai-half.turn { width: calc(75% - 10.5px); }
.ai-half.right { justify-content: flex-end; }
.ai-half.black { background: #5a5e63; color: #fff; }
.ai-half.white { background: #fff; color: #2b2d30; }

/* A switch keeps one look in both states: the opposite colour of its half; the knob and the text
   move. */
button.ai-switch {
  position: relative;
  transition: background-color .2s ease;
  flex: none;
  width: 72px;
  height: 26px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 13px;
  font-family: inherit;
  font-size: 12px;
  line-height: 26px;
  font-weight: bold;
  cursor: pointer;
}

.ai-half.black button.ai-switch { background: #fff; color: #2b2d30; }
.ai-half.white button.ai-switch { background: #5a5e63; color: #fff; }

/* AI 自动 is violet, the AI's colour on the board: full violet on the white half; on the dark half
   a light violet (full violet is hardly darker than the half itself), with dark violet text. */
.ai-half.white button.ai-switch.on { background: #8e44ad; color: #fff; }
.ai-half.black button.ai-switch.on { background: #dcc5f0; color: #4a1f6b; }

.ai-switch .knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  box-sizing: border-box;
  box-shadow: 0 1px 2px #0000004d;
  transition: left .2s ease;
}

.ai-switch.on .knob { left: 49px; }
.ai-half.black .ai-switch .knob { background: #000; }
.ai-half.white .ai-switch .knob { background: #fff; }
.ai-switch .label { position: absolute; top: 0; right: 10px; line-height: 26px; }
.ai-switch.on .label { right: auto; left: 8px; }

.ai-middle {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  box-sizing: border-box;
  width: 112px;
  text-align: center;
  white-space: nowrap;
  font-size: 13px;
}

.ai-thinking.black { color: #fff; }
.ai-thinking.white { color: #2b2d30; }

button.ai-middle {
  height: 28px;
  margin: 0;
  padding: 0 6px;
  line-height: 26px;
  border-radius: 5px;
  border: 1px solid #5a5e63;
  background: #fff;
  color: #2b2d30;
  font-family: inherit;
  cursor: pointer;
}

button.ai-middle.black { border-color: #fff; }
button.ai-continue { font-weight: bold; }

.ai-thinking .spinner {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-left: 6px;
  vertical-align: -1px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  box-sizing: border-box;
  animation: ai-spin 0.8s linear infinite;
}

@keyframes ai-spin { to { transform: rotate(360deg); } }

/* Practice (SPEC §6.7). */
.practice-head {
  margin-top: 6px;
  color: #2c3e50;
  font-size: 17px;
  font-weight: bold;
}

.practice-verdict {
  margin: 6px auto 2px;
  color: #2c3e50;
  font-size: 16px;
  font-weight: bold;
}

/* Green is kept for results (a good answer). */
.practice-verdict.good {
  color: #1a7f37;
}

/* 对比学习 and 练习模式 (and in practice 看答案 and 结束练习) open or leave a mode, so they look unlike
   the jumps beside them: a light wood fill with a wood-brown outline and text (not the main
   button's solid brown). */
button.mode-switch {
  background: #f6ead6;
  border-color: #8b5e34;
  color: #8b5e34;
  font-weight: bold;
}

.estimate-katago {
  margin-top: 8px;
  color: #2c3e50;
  font-size: 15px;
}

button.suggestions-switch {
  width: auto;
  height: auto;
  padding: 2px 4px;
  border: none;
  background: none;
  color: #999;
  font-size: 12px;
  cursor: pointer;
}

button.suggestions-switch:hover {
  color: #595959;
  text-decoration: underline;
}
</style>
